import { API, API_BASE } from '../config/api';
import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, AlertCircle, Loader2, Download, Home, Mail, FolderDown, ArrowDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';


export default function Success() {
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const sessionId = searchParams.get('session_id');

  const pollPaymentStatus = useCallback(async (attempt = 0) => {
    const maxAttempts = 8;
    const pollInterval = 2000;

    if (attempt >= maxAttempts) {
      setStatus('failed');
      return;
    }

    try {
      const { data } = await axios.get(`${API}/checkout/status/${sessionId}`);

      if (data.payment_status === 'paid') {
        setStatus('success');
        setPaymentData(data);

        // ── Snippet Unlock: persist unlocked track id so player skips the 30s cap ──
        if (data.metadata?.unlock_item_id || searchParams.get('unlock')) {
          try {
            const unlockedRaw = localStorage.getItem('mp_unlockedTrackIds') || '[]';
            const arr = JSON.parse(unlockedRaw);
            const unlockId = data.metadata?.unlock_item_id;
            if (unlockId && !arr.includes(String(unlockId))) {
              arr.push(String(unlockId));
              localStorage.setItem('mp_unlockedTrackIds', JSON.stringify(arr));
            }
            localStorage.removeItem('mp_pendingUnlock');
          } catch (e) { /* ignore */ }
        }

        // Fetch the download link
        if (data.metadata?.track_id) {
          try {
            const dlRes = await axios.get(`${API}/checkout/download-link/${sessionId}`);
            if (dlRes.data?.download_url) {
              // Backend returns relative path like /api/download/token
              // Prepend the correct origin so it works in any environment
              const url = dlRes.data.download_url;
              setDownloadUrl(url.startsWith('/') ? `${API_BASE}${url}` : url);
            }
          } catch (e) {
            console.log('Download link will be sent via email');
          }
        }
        return;
      } else if (data.status === 'expired') {
        setStatus('failed');
        return;
      }

      setTimeout(() => pollPaymentStatus(attempt + 1), pollInterval);
    } catch (e) {
      console.error('Error checking payment status:', e);
      setTimeout(() => pollPaymentStatus(attempt + 1), pollInterval);
    }
  }, [sessionId]);

  useEffect(() => {
    if (sessionId) {
      pollPaymentStatus(0);
    } else {
      setStatus('failed');
    }
  }, [sessionId, pollPaymentStatus]);

  const handleDownload = async () => {
    if (!downloadUrl) {
      toast.info('Download link will be sent to your email.');
      return;
    }
    setDownloading(true);
    try {
      // Use fetch + blob to trigger a real file download (avoids popup blockers)
      const response = await fetch(downloadUrl);
      if (!response.ok) throw new Error('Download failed');
      const blob = await response.blob();
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = paymentData?.metadata?.track_title
        ? `${paymentData.metadata.track_title}.mp3`
        : 'download.mp3';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?(.+?)"?$/);
        if (match) filename = match[1];
      }
      const blobUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(blobUrl);
      setDownloaded(true);
      toast.success('Download complete!');
    } catch (err) {
      console.error('Download error:', err);
      // Fallback: direct navigation
      window.location.href = downloadUrl;
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4" data-testid="success-page">
      <div className="max-w-md w-full bg-[#141414] border border-[#262626] p-8 text-center">
        {status === 'checking' && (
          <>
            <Loader2 className="w-16 h-16 mx-auto text-[#FF3B30] animate-spin mb-6" />
            <h1 className="font-cabinet text-2xl font-black uppercase tracking-tight mb-4">
              VERIFYING PAYMENT
            </h1>
            <p className="text-[#A3A3A3]">Please wait while we confirm your purchase...</p>
          </>
        )}

        {status === 'success' && (
          <>
            <div className="w-16 h-16 mx-auto bg-[#22C55E] flex items-center justify-center mb-6">
              <CheckCircle className="w-10 h-10 text-white" />
            </div>
            <h1 className="font-cabinet text-2xl font-black uppercase tracking-tight mb-4">
              PAYMENT SUCCESSFUL
            </h1>
            <p className="text-[#A3A3A3] mb-2">
              Thank you for your purchase!{downloadUrl ? ' Your download is ready.' : ''}
            </p>
            <p className="text-[#DAA520] text-sm mb-6">
              Your support means the world. I.O.M.S.L
            </p>

            {paymentData?.metadata?.track_title && (
              <div className="bg-[#0A0A0A] border border-[#262626] p-4 mb-6">
                <p className="overline mb-1">PURCHASED</p>
                <p className="font-cabinet text-lg font-bold uppercase">
                  {paymentData.metadata.track_title}
                </p>
                <p className="font-mono text-[#FF3B30]">
                  ${(paymentData.amount_total / 100).toFixed(2)} {paymentData.currency?.toUpperCase()}
                </p>
              </div>
            )}

            <div className="flex flex-col gap-3">
              {downloadUrl ? (
                <>
                  <Button
                    onClick={handleDownload}
                    disabled={downloading}
                    className="w-full bg-[#FF3B30] hover:bg-white hover:text-[#0A0A0A] rounded-none"
                    data-testid="download-btn"
                  >
                    {downloading ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="w-4 h-4 mr-2" />
                    )}
                    {downloading ? 'DOWNLOADING...' : downloaded ? 'DOWNLOAD AGAIN' : 'DOWNLOAD NOW'}
                  </Button>
                  {downloaded && (
                    <div className="bg-[#0A0A0A] border border-[#22C55E]/30 p-4 text-left animate-in fade-in slide-in-from-bottom-2" data-testid="download-complete-notice">
                      <div className="flex items-center gap-2 mb-2">
                        <FolderDown className="w-4 h-4 text-[#22C55E]" />
                        <span className="text-sm font-bold text-[#22C55E]">Download Complete</span>
                      </div>
                      <p className="text-xs text-[#A3A3A3]">
                        Your file has been saved to your device's <strong className="text-[#E5E5E5]">Downloads</strong> folder.
                        Check your browser's download bar at the bottom of the screen, or open your file manager.
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="bg-[#0A0A0A] border border-[#262626] p-4 text-left">
                  <div className="flex items-center gap-2 mb-2">
                    <Mail className="w-4 h-4 text-[#22C55E]" />
                    <span className="text-sm font-bold text-[#E5E5E5]">Download link sent to your email</span>
                  </div>
                  <p className="text-xs text-[#737373]">Check your inbox for the download link. It expires in 24 hours.</p>
                </div>
              )}
              <Link to="/">
                <Button
                  variant="outline"
                  className="w-full rounded-none border-[#262626] hover:border-[#FF3B30]"
                  data-testid="back-home-btn"
                >
                  <Home className="w-4 h-4 mr-2" />
                  BACK TO HOME
                </Button>
              </Link>
              <Link to="/my-purchases" className="block">
                <span className="text-xs text-[#737373] hover:text-[#DAA520] transition-colors underline underline-offset-2" data-testid="my-purchases-link">
                  View all your purchases
                </span>
              </Link>
            </div>
          </>
        )}

        {status === 'failed' && (
          <>
            <div className="w-16 h-16 mx-auto bg-[#FF3B30] flex items-center justify-center mb-6">
              <AlertCircle className="w-10 h-10 text-white" />
            </div>
            <h1 className="font-cabinet text-2xl font-black uppercase tracking-tight mb-4">
              PAYMENT FAILED
            </h1>
            <p className="text-[#A3A3A3] mb-6">
              Something went wrong with your payment. Please try again.
            </p>
            <Link to="/">
              <Button
                className="w-full bg-[#FF3B30] hover:bg-white hover:text-[#0A0A0A] rounded-none"
                data-testid="try-again-btn"
              >
                TRY AGAIN
              </Button>
            </Link>
          </>
        )}
      </div>
    </div>
  );
}
